import { useEffect, useMemo, useReducer, useState } from 'react';
import { motion } from 'framer-motion';
import { Header } from './components/Header.jsx';
import { TaskComposer } from './components/TaskComposer.jsx';
import { AgentRoster } from './components/AgentRoster.jsx';
import { SynthesisPanel } from './components/SynthesisPanel.jsx';
import { EstimatePanel } from './components/EstimatePanel.jsx';
import { CostVisualizer } from './components/CostVisualizer.jsx';
import { RecommenderCard } from './components/RecommenderCard.jsx';
import { ResponseTimeline } from './components/ResponseTimeline.jsx';
import { StatsDock } from './components/StatsDock.jsx';
import { RunBar } from './components/RunBar.jsx';

import { TEMPLATES, fillTemplate, templateById } from './lib/templates.js';
import { recommendFor } from './lib/recommender.js';
import { orchestrationEstimate, costFor, estimateInputTokens } from './lib/estimate.js';
import { api, pollHealth } from './lib/api.js';
import { MODELS } from './lib/models.js';

const DEFAULT_AGENTS = [
  { id: 'a1', model: 'gpt-4o-mini', systemPrompt: 'You are a pragmatic engineer focused on shipping clean, correct solutions.' },
  { id: 'a2', model: 'deepseek-chat', systemPrompt: 'You are a systems architect focused on long-term flexibility and clarity.' },
  { id: 'a3', model: 'claude-sonnet', systemPrompt: 'You are a thoughtful generalist who balances trade-offs and explains reasoning.' },
];

const DEFAULT_JUDGE_PROMPT =
  'You are an impartial judge. Read the task and every agent response, then either pick the strongest answer or compose a synthesis that combines the best ideas. Be specific about which agents contributed which ideas.';

function agentReducer(state, action) {
  switch (action.type) {
    case 'set':
      return action.agents;
    case 'patch': {
      const next = state.slice();
      next[action.index] = { ...next[action.index], ...action.patch };
      return next;
    }
    case 'add': {
      if (state.length >= 6) return state;
      const id = `a${Date.now()}`;
      return [
        ...state,
        { id, model: 'claude-haiku', systemPrompt: 'You are a careful, concise reviewer.' },
      ];
    }
    case 'remove':
      return state.filter((_, i) => i !== action.index);
    case 'resetPrompts': {
      const tpl = templateById(action.templateId);
      const presets = tpl?.systemPrompts || [];
      return state.map((a, i) => ({
        ...a,
        systemPrompt: presets[i] || DEFAULT_AGENTS[i]?.systemPrompt || a.systemPrompt,
      }));
    }
    default:
      return state;
  }
}

export default function App() {
  // Task state
  const [templateId, setTemplateId] = useState('compare-approaches');
  const [fieldValues, setFieldValues] = useState({ topic: 'choosing a queue: SQS vs Kafka vs Redis Streams' });
  const [freeformTask, setFreeformTask] = useState('');
  const [maxTokens, setMaxTokens] = useState(700);
  const [maxTier, setMaxTier] = useState(3);
  const [runsPerDay, setRunsPerDay] = useState(50);

  // Agents
  const [agents, dispatch] = useReducer(agentReducer, DEFAULT_AGENTS);

  // Synthesis
  const [synthesis, setSynthesis] = useState(true);
  const [judgeKey, setJudgeKey] = useState('claude-sonnet');
  const [judgePrompt, setJudgePrompt] = useState(DEFAULT_JUDGE_PROMPT);

  // Run
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Gateway health/stats
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [latency, setLatency] = useState(null);

  // Apply template defaults when template changes
  useEffect(() => {
    const tpl = templateById(templateId);
    if (!tpl) return;
    setMaxTokens(tpl.suggestedMaxTokens);
    // top up agent count to suggestedAgents
    if (agents.length < tpl.suggestedAgents) {
      const need = tpl.suggestedAgents - agents.length;
      for (let i = 0; i < need; i++) dispatch({ type: 'add' });
    }
    dispatch({ type: 'resetPrompts', templateId });
    // reset field values for new template's fields, preserving keys that exist
    setFieldValues((prev) => {
      const next = {};
      for (const f of tpl.fields || []) next[f.key] = prev[f.key] ?? '';
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Health polling
  useEffect(() => {
    const stop = pollHealth((tick) => {
      setHealth(tick);
      if (tick.ok) {
        const t0 = performance.now();
        api.health().then(() => setLatency(Math.round(performance.now() - t0))).catch(() => {});
      }
    }, 30_000);
    return stop;
  }, []);

  // Stats fetch (best-effort)
  useEffect(() => {
    let cancelled = false;
    api.stats()
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Resolved task (from template + fields, or freeform)
  const tpl = templateById(templateId);
  const resolvedTask = useMemo(() => {
    if (!tpl) return freeformTask;
    return fillTemplate(tpl, fieldValues);
  }, [tpl, fieldValues, freeformTask]);

  // Estimate
  const estimate = useMemo(() => {
    return orchestrationEstimate({
      task: resolvedTask,
      systemPrompts: agents.map((a) => a.systemPrompt),
      modelKeys: agents.map((a) => a.model),
      maxTokens,
      synthesis,
      judgeKey,
    });
  }, [resolvedTask, agents, maxTokens, synthesis, judgeKey]);

  // Recommendations
  const recs = useMemo(() => recommendFor(resolvedTask), [resolvedTask]);

  const canRun = !running && resolvedTask.trim().length > 4 && agents.length > 0;

  async function runOrchestration() {
    setError(null);
    setRunning(true);
    setResult(null);
    const startedAt = performance.now();

    try {
      const body = {
        task: resolvedTask,
        agents: agents.length,
        models: agents.map((a) => a.model),
        system_prompts: agents.map((a) => a.systemPrompt),
        max_tokens: maxTokens,
        synthesis: synthesis ? { enabled: true, judge_model: judgeKey, judge_prompt: judgePrompt } : { enabled: false },
      };

      const data = await api.orchestrate(body);

      // Normalize response shape to what UI expects
      const responses = Array.isArray(data?.responses)
        ? data.responses
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data) ? data : [];

      const enriched = responses.map((r, i) => {
        const model = r.model || agents[i]?.model;
        const inputTokens = r.tokens?.input ?? r.input_tokens ?? estimateInputTokens(resolvedTask + (agents[i]?.systemPrompt || ''));
        const outputTokens = r.tokens?.output ?? r.output_tokens ?? Math.round((r.content || '').length / 3.8);
        return {
          model,
          content: r.content || r.text || r.response || JSON.stringify(r, null, 2),
          tokens: { input: inputTokens, output: outputTokens },
          cost: r.cost ?? costFor(model, inputTokens, outputTokens),
          elapsedMs: r.elapsedMs ?? r.elapsed_ms ?? null,
        };
      });

      let synth = null;
      if (synthesis) {
        const s = data?.synthesis || data?.judge;
        if (s) {
          synth = {
            content: s.content || s.text || '',
            rationale: s.rationale || null,
            winnerIndex: typeof s.winnerIndex === 'number' ? s.winnerIndex : (typeof s.winner_index === 'number' ? s.winner_index : null),
            judgeModel: s.model || judgeKey,
          };
        }
      }

      const elapsedMs = Math.round(performance.now() - startedAt);
      const totalCost = enriched.reduce((a, b) => a + (b.cost || 0), 0);
      const totalTokens = enriched.reduce((a, b) => a + (b.tokens?.input || 0) + (b.tokens?.output || 0), 0);

      setResult({
        responses: enriched,
        synthesis: synth,
        totals: { elapsedMs, totalCost, totalTokens },
      });

      // refresh stats after run
      api.stats().then(setStats).catch(() => {});
    } catch (e) {
      setError(e?.message || 'Orchestration failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg-base">
      <Header health={health} latencyMs={latency} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative mx-auto max-w-[1400px] px-6 pb-6 pt-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <span className="chip-cyan mb-3">
              <span className="font-mono">multi-model · parallel · cost-aware</span>
            </span>
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight md:text-5xl">
              Orchestrate <span className="text-gradient-cyan-orange">N agents</span> against a single task,
              <br className="hidden md:inline" /> then synthesize the best answer.
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-ink-muted md:text-base">
              Pick a template, customize each agent's perspective, see live token & cost projections, and
              ship the polished judge synthesis. All routed through your LiteLLM gateway and traced to
              Langfuse Cloud.
            </p>
          </motion.div>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] space-y-5 px-6 pb-20">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr),360px]">
          <div className="space-y-5">
            <TaskComposer
              templateId={templateId}
              onTemplateChange={setTemplateId}
              fieldValues={fieldValues}
              onFieldChange={(k, v) => setFieldValues((prev) => ({ ...prev, [k]: v }))}
              task={freeformTask}
              onTaskChange={setFreeformTask}
              maxTokens={maxTokens}
              onMaxTokensChange={setMaxTokens}
            />
            <AgentRoster
              agents={agents}
              onChange={(index, patch) => dispatch({ type: 'patch', index, patch })}
              onAdd={() => dispatch({ type: 'add' })}
              onRemove={(index) => dispatch({ type: 'remove', index })}
              onResetSystemPrompts={() => dispatch({ type: 'resetPrompts', templateId })}
              maxTier={maxTier}
              onMaxTierChange={setMaxTier}
            />
            <SynthesisPanel
              enabled={synthesis}
              onToggle={setSynthesis}
              judgeKey={judgeKey}
              onJudgeChange={setJudgeKey}
              judgePrompt={judgePrompt}
              onJudgePromptChange={setJudgePrompt}
            />
          </div>
          <aside className="space-y-5">
            <EstimatePanel estimate={estimate} runsPerDay={runsPerDay} />
            <RecommenderCard recs={recs} />
          </aside>
        </div>

        <CostVisualizer
          task={resolvedTask}
          maxTokens={maxTokens}
          runsPerDay={runsPerDay}
          currentModelKeys={agents.map((a) => a.model)}
          maxTier={maxTier}
        />

        <RunBar
          canRun={canRun}
          running={running}
          onRun={runOrchestration}
          error={error}
          runsPerDay={runsPerDay}
          onRunsPerDayChange={setRunsPerDay}
        />

        <ResponseTimeline result={result} running={running} />

        <StatsDock stats={stats} />
      </main>

      <footer className="mx-auto max-w-[1400px] px-6 pb-10 text-center font-mono text-[11px] text-ink-dim">
        BrackenW3 Orchestrator · gateway via LiteLLM · traces in Langfuse Cloud
      </footer>
    </div>
  );
}
